import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Trophy, MapPin, Loader2, Award, Share2, Search, X, Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { Input } from "../components/ui/input";
import api from "../lib/api";

/**
 * /team — Public showcase of opted-in verified Vermittlers.
 * No auth required. Buyers see who's actively referring on TwoWheels and can
 * click through to a vermittler's personal share-link.
 *
 * Active filters (q, city, brand) are persisted to the URL as query
 * parameters so the filtered view is deep-linkable and shareable.
 */
export default function TeamShowcasePage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchParams, setSearchParams] = useSearchParams();
    const query = searchParams.get("q") || "";
    const cityFilter = searchParams.get("city") || "";
    const brandFilter = searchParams.get("brand") || "";

    // Single helper to update one filter at a time, preserving the others.
    const setFilter = (key, value) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (value) next.set(key, value);
            else next.delete(key);
            return next;
        }, { replace: true });
    };
    const setQuery = (v) => setFilter("q", v);
    const setCityFilter = (v) => setFilter("city", v);
    const setBrandFilter = (v) => setFilter("brand", v);

    useEffect(() => {
        api.get("/team/showcase")
            .then((r) => setItems(r.data.items || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, []);

    const cities = useMemo(() => {
        const counts = new Map();
        items.forEach((v) => v.city && counts.set(v.city, (counts.get(v.city) || 0) + 1));
        return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 12);
    }, [items]);

    const brands = useMemo(() => {
        const counts = new Map();
        items.forEach((v) => (v.favorite_brands || []).forEach((b) => counts.set(b, (counts.get(b) || 0) + 1)));
        return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 12);
    }, [items]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return items.filter((v) => {
            if (cityFilter && (v.city || "").toLowerCase() !== cityFilter.toLowerCase()) return false;
            if (brandFilter && !(v.favorite_brands || []).map((b) => b.toLowerCase()).includes(brandFilter.toLowerCase())) return false;
            if (q) {
                const hay = `${v.name || ""} ${v.city || ""} ${(v.favorite_brands || []).join(" ")}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [items, query, cityFilter, brandFilter]);

    const hasFilters = !!(query || cityFilter || brandFilter);
    const clearFilters = () => {
        setSearchParams(new URLSearchParams(), { replace: true });
    };

    return (
        <div className="min-h-screen bg-[#FAFAFA]" data-testid="team-page">
            {/* Hero */}
            <section className="bg-[#111827] text-white py-16 md:py-24">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <div className="flex items-center gap-3 mb-3">
                        <Trophy className="w-8 h-8 text-[#FFCC00]" />
                        <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#FFCC00]">
                            TwoWheels Vermittler-Netzwerk
                        </span>
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight max-w-3xl">
                        Unsere besten Vermittler bringen Käufer &amp; Verkäufer zusammen.
                    </h1>
                    <p className="text-base mt-4 max-w-2xl text-gray-300">
                        Verifizierte TwoWheels-Vermittler aus ganz Deutschland — sie kennen den
                        Markt, vermitteln Fahrzeuge und werden für jeden erfolgreichen Deal belohnt.
                    </p>
                </div>
            </section>

            {/* Social-Proof Bewertungs-Slider */}
            <TestimonialSlider />

            {/* Grid */}
            <section className="max-w-7xl mx-auto px-4 md:px-8 py-12">
                {loading ? (
                    <div className="text-center py-16" data-testid="team-loading">
                        <Loader2 className="w-8 h-8 animate-spin text-[#FFCC00] mx-auto" />
                    </div>
                ) : items.length === 0 ? (
                    <div className="bg-white border border-gray-200 rounded-sm p-12 text-center" data-testid="team-empty">
                        <Trophy className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <h2 className="font-display font-bold text-xl mb-2">Bald geht's los!</h2>
                        <p className="text-sm text-gray-600 max-w-md mx-auto">
                            Aktuell ist noch kein Vermittler im öffentlichen Showcase. Sobald
                            unsere ersten Tippgeber sich freischalten, erscheinen sie hier.
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Filter Bar */}
                        <div className="bg-white border border-gray-200 rounded-sm p-4 mb-6" data-testid="team-filters">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="relative flex-1">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                    <Input
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        placeholder="Name, Stadt oder Marke suchen…"
                                        className="rounded-sm pl-9"
                                        data-testid="team-search-input"
                                    />
                                </div>
                                {hasFilters && (
                                    <button
                                        onClick={clearFilters}
                                        className="inline-flex items-center gap-1 px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-sm border border-gray-300 hover:border-[#111827]"
                                        data-testid="team-clear-filters"
                                    >
                                        <X className="w-3 h-3" /> Zurücksetzen
                                    </button>
                                )}
                            </div>

                            {cities.length > 0 && (
                                <div className="mb-2" data-testid="team-city-pills">
                                    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">Stadt</div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {cities.map(([city, n]) => {
                                            const active = cityFilter === city;
                                            return (
                                                <button
                                                    key={city}
                                                    onClick={() => setCityFilter(active ? "" : city)}
                                                    className={`px-2.5 py-1 rounded-sm text-xs font-bold uppercase tracking-wider border transition-colors ${active ? "bg-[#FFCC00] text-[#111827] border-[#FFCC00]" : "bg-white text-gray-700 border-gray-300 hover:border-[#111827]"}`}
                                                    data-testid={`team-filter-city-${city}`}
                                                >
                                                    {city} <span className="text-gray-400 font-mono-num">·{n}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {brands.length > 0 && (
                                <div data-testid="team-brand-pills">
                                    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">Marke</div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {brands.map(([brand, n]) => {
                                            const active = brandFilter === brand;
                                            return (
                                                <button
                                                    key={brand}
                                                    onClick={() => setBrandFilter(active ? "" : brand)}
                                                    className={`px-2.5 py-1 rounded-sm text-xs font-bold uppercase tracking-wider border transition-colors ${active ? "bg-[#FFCC00] text-[#111827] border-[#FFCC00]" : "bg-white text-gray-700 border-gray-300 hover:border-[#111827]"}`}
                                                    data-testid={`team-filter-brand-${brand}`}
                                                >
                                                    {brand} <span className="text-gray-400 font-mono-num">·{n}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex items-baseline justify-between mb-6">
                            <h2 className="font-display text-2xl font-black" data-testid="team-count">
                                {filtered.length === items.length
                                    ? `${items.length} aktive Vermittler`
                                    : `${filtered.length} von ${items.length} Vermittlern`}
                            </h2>
                            <span className="text-xs uppercase tracking-wider text-gray-500 font-bold">
                                Sortiert nach Vermittlungen
                            </span>
                        </div>

                        {filtered.length === 0 ? (
                            <div className="bg-white border border-gray-200 rounded-sm p-8 text-center" data-testid="team-no-results">
                                <p className="text-sm text-gray-500">Keine Treffer für die gewählten Filter. Bitte Suche anpassen oder zurücksetzen.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="team-grid">
                                {filtered.map((v, i) => (
                                    <TeamCard key={v.id} v={v} rank={items.indexOf(v) + 1} />
                                ))}
                            </div>
                        )}
                    </>
                )}
            </section>

            {/* CTA — Become a Vermittler */}
            <section className="bg-white border-t border-gray-200 py-12">
                <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
                    <Award className="w-10 h-10 text-[#FFCC00] mx-auto mb-3" />
                    <h2 className="font-display text-2xl font-black mb-2">
                        Selbst zum Vermittler werden?
                    </h2>
                    <p className="text-sm text-gray-600 mb-5">
                        Empfehlen Sie TwoWheels in Ihrem Netzwerk und verdienen Sie für jede
                        erfolgreiche Vermittlung Punkte — auszahlbar als Bank-Überweisung oder PayPal.
                    </p>
                    <a
                        href="/register?role=vermittler"
                        className="inline-block bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold uppercase tracking-wider rounded-sm px-6 py-3 text-sm"
                        data-testid="team-cta-register"
                    >
                        Jetzt als Vermittler starten
                    </a>
                </div>
            </section>
        </div>
    );
}

function TestimonialSlider() {
    const [items, setItems] = useState([]);
    const [idx, setIdx] = useState(0);

    useEffect(() => {
        api.get("/team/testimonials", { params: { limit: 12 } })
            .then((r) => setItems(r.data.items || []))
            .catch(() => setItems([]));
    }, []);

    useEffect(() => {
        if (items.length < 2) return;
        const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 5500);
        return () => clearInterval(t);
    }, [items.length]);

    if (items.length === 0) return null;
    const t = items[idx];
    const initials = (t.reviewer_name || "?").split(" ").map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
    const go = (dir) => setIdx((i) => (i + dir + items.length) % items.length);

    return (
        <section className="bg-[#FFCC00]/10 border-y border-[#FFCC00]/40 py-10" data-testid="team-testimonials">
            <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
                <div className="flex items-center justify-center gap-2 mb-4">
                    <Quote className="w-5 h-5 text-[#FFCC00]" />
                    <span className="text-xs uppercase tracking-[0.2em] font-bold text-gray-600">Das sagen zufriedene Nutzer</span>
                </div>
                <div className="relative min-h-[150px] flex flex-col items-center justify-center" data-testid="team-testimonial-card" key={idx}>
                    <div className="flex items-center gap-0.5 mb-3" data-testid="team-testimonial-rating">
                        {Array.from({ length: 5 }).map((_, i) => (
                            <Star key={i} className={`w-4 h-4 ${i < t.rating ? "text-[#FFCC00] fill-[#FFCC00]" : "text-gray-300"}`} />
                        ))}
                    </div>
                    {t.title && <div className="font-display font-black text-lg mb-1" data-testid="team-testimonial-title">{t.title}</div>}
                    <p className="text-base text-gray-700 italic max-w-2xl" data-testid="team-testimonial-comment">„{t.comment}"</p>
                    <div className="flex items-center gap-2 mt-4">
                        <div className="w-8 h-8 rounded-full bg-[#111827] text-[#FFCC00] flex items-center justify-center text-xs font-black">{initials}</div>
                        <span className="text-sm font-bold text-[#111827]">{t.reviewer_name}</span>
                    </div>
                </div>
                {items.length > 1 && (
                    <div className="flex items-center justify-center gap-3 mt-5">
                        <button type="button" onClick={() => go(-1)} className="w-8 h-8 rounded-full border border-gray-300 hover:border-[#111827] flex items-center justify-center" data-testid="team-testimonial-prev" aria-label="Zurück"><ChevronLeft className="w-4 h-4" /></button>
                        <div className="flex items-center gap-1.5">
                            {items.map((_, i) => (
                                <button key={i} type="button" onClick={() => setIdx(i)} className={`w-2 h-2 rounded-full transition-colors ${i === idx ? "bg-[#111827]" : "bg-gray-300"}`} data-testid={`team-testimonial-dot-${i}`} aria-label={`Bewertung ${i + 1}`} />
                            ))}
                        </div>
                        <button type="button" onClick={() => go(1)} className="w-8 h-8 rounded-full border border-gray-300 hover:border-[#111827] flex items-center justify-center" data-testid="team-testimonial-next" aria-label="Weiter"><ChevronRight className="w-4 h-4" /></button>
                    </div>
                )}
            </div>
        </section>
    );
}

function TeamCard({ v, rank }) {
    const MEDAL = ["🥇", "🥈", "🥉"];
    const initials = (v.name || "?").split(" ").map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
    const shareUrl = v.referral_short_code ? `/v/${v.referral_short_code}` : null;
    return (
        <div className="bg-white border border-gray-200 rounded-sm p-5 hover:border-[#FFCC00] transition-colors" data-testid={`team-card-${v.id}`}>
            <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-sm bg-[#111827] text-[#FFCC00] flex items-center justify-center font-display font-black text-lg shrink-0 relative overflow-hidden">
                    {v.profile_photo_url ? (
                        <img src={v.profile_photo_url} alt="" className="w-full h-full object-cover" />
                    ) : v.company_logo_url ? (
                        <img src={v.company_logo_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                        initials
                    )}
                    {rank <= 3 && (
                        <span className="absolute -top-2 -right-2 text-xl select-none">{MEDAL[rank - 1]}</span>
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-[#111827] truncate">{v.name || "TwoWheels-Vermittler"}</div>
                    {v.city && (
                        <div className="flex items-center gap-1 text-[11px] text-gray-500 mt-0.5">
                            <MapPin className="w-3 h-3" /> {v.city}
                        </div>
                    )}
                </div>
                <span className="text-[10px] font-mono-num font-bold text-gray-400">#{rank}</span>
            </div>
            <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
                <div>
                    <div className="text-2xl font-black font-mono-num text-[#FFCC00]">{v.referral_count}</div>
                    <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Vermittlungen</div>
                </div>
                {shareUrl && (
                    <a
                        href={shareUrl}
                        className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-[#111827] hover:text-[#FFCC00]"
                        data-testid={`team-share-${v.id}`}
                    >
                        <Share2 className="w-3.5 h-3.5" /> Link öffnen
                    </a>
                )}
            </div>
        </div>
    );
}
